import { GranularityEnum } from '@/src/enums/granularity.enum'
import { addDays, addHours, addMinutes, addMonths, format, isAfter, isSameDay, isSameHour, isSameMinute, isSameMonth } from 'date-fns'
import { de } from 'date-fns/locale'
import { t } from 'i18next'
interface IFormError {
  field: string
  msg: string
}

interface ICustomDataForm {
  title: string
  details: string
  aggregationType: string
  unit: string
  granularity: GranularityEnum
  startDate: Date
  endDate: Date
  locationId: number
}

export const validateCustomDataFormValues = (formValues: ICustomDataForm): IFormError[] => {
  const errors: IFormError[] = []

  // Validate title
  if (!formValues.title || formValues.title.trim().length === 0) {
    errors.push({ field: 'title', msg: 'Title ist erforderlich.' })
  }

  // Validate type
  if (!formValues.aggregationType || formValues.aggregationType.trim().length === 0) {
    errors.push({ field: 'type', msg: 'Type ist erforderlich.' })
  }

  // Validate unit
  if (!formValues.unit || formValues.unit.trim().length === 0) {
    errors.push({ field: 'unit', msg: 'Unit ist erforderlich.' })
  }

  // Validate granularity
  if (!Object.values(GranularityEnum).includes(formValues.granularity)) {
    errors.push({ field: 'granularity', msg: t('data_management.granularity_invalid') })
  }

  // Validate start and end date
  if (!(formValues.startDate instanceof Date && !Number.isNaN(formValues.startDate.getTime()))) {
    errors.push({ field: 'startDate', msg: t('data_management.start_date_invalid') })
  }
  if (!(formValues.endDate instanceof Date && !Number.isNaN(formValues.endDate.getTime()))) {
    errors.push({ field: 'endDate', msg: t('data_management.end_date_invalid') })
  }

  // Check if the end date is after the start date
  if (formValues.startDate > formValues.endDate) {
    errors.push({
      field: 'endDate',
      msg: 'Das Enddatum muss nach dem Startdatum liegen.',
    })
  }

  // Validate locationId
  if (typeof formValues.locationId !== 'number' || formValues.locationId <= 0) {
    errors.push({ field: 'locationId', msg: t('data_management.location_invalid') })
  }

  return errors
}

export const getCustomTimeseriesIntervalLabel = (date: Date, granularity: GranularityEnum | ''): string => {
  switch (granularity) {
    case GranularityEnum.MINUTES:
      return format(date, 'dd LLLL yyyy HH:mm', { locale: de }) // 24 March 2024 00:00
    case GranularityEnum.FIFTEEN_MINUTES:
      return format(date, 'dd LLLL yyyy HH:mm', { locale: de }) // 24 March 2024 00:00
    case GranularityEnum.ONE_HOUR:
      return format(date, 'dd LLLL yyyy HH:mm', { locale: de }) // 24 March 2024 00:00
    case GranularityEnum.ONE_DAY:
      return format(date, 'EEEE, dd LLLL yyyy', { locale: de }) // 24 March 2024
    case GranularityEnum.ONE_WEEK:
      return `KW ${format(date, 'w yyyy', { locale: de })}` // 24 March 2024
    case GranularityEnum.ONE_MONTH:
      return format(date, 'MMMM yyyy', { locale: de }) // March 2024
    default:
      return format(date, 'dd.MM.yyyy', { locale: de }) // 24.03.2024
  }
}

const addGranularity = (date, granularity) => {
  switch (granularity) {
    case GranularityEnum.FIFTEEN_MINUTES:
      return addMinutes(date, 15)
    case GranularityEnum.ONE_HOUR:
      return addHours(date, 1)
    case GranularityEnum.ONE_DAY:
      return addDays(date, 1)
    case GranularityEnum.ONE_WEEK:
      return addDays(date, 7)
    case GranularityEnum.ONE_MONTH:
      return addMonths(date, 1)
    default:
      throw new Error('Unsupported granularity')
  }
}

const shouldAdd = (currentDate, endDate, granularity) => {
  switch (granularity) {
    case GranularityEnum.FIFTEEN_MINUTES:
      return !isAfter(currentDate, endDate) || isSameMinute(currentDate, endDate)
    case GranularityEnum.ONE_HOUR:
      return !isAfter(currentDate, endDate) || isSameHour(currentDate, endDate)
    case GranularityEnum.ONE_DAY:
      return !isAfter(currentDate, endDate) || isSameDay(currentDate, endDate)
    case GranularityEnum.ONE_WEEK:
      return !isAfter(currentDate, endDate) || isSameDay(currentDate, endDate)
    case GranularityEnum.ONE_MONTH:
      return !isAfter(currentDate, endDate) || isSameMonth(currentDate, endDate)
    default:
      throw new Error('Unsupported granularity', granularity)
  }
}

export function generateCustomTimeseriesIntervals(startDate, endDate, granularity) {
  const intervals: string[] = []
  let currentDate = new Date(startDate)

  while (shouldAdd(currentDate, endDate, granularity)) {
    intervals.push(getCustomTimeseriesIntervalLabel(currentDate, granularity))
    currentDate = addGranularity(currentDate, granularity)
  }

  return intervals
}
